import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PublicPaymentOptions {
  deposit_percent: number;
  deposit_amount: number;
  total_amount: number;
}

// The deposit and full amounts a customer can choose between on the public
// quote page (#983). Resolved by square-payments so the page shows the same
// numbers the charge uses. Stays null while loading or on any failure, and the
// page then offers the deposit alone, exactly as before this choice existed.
export function usePublicPaymentOptions(quoteId: string | undefined, enabled: boolean) {
  const [options, setOptions] = useState<PublicPaymentOptions | null>(null);

  useEffect(() => {
    if (!quoteId || !enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("square-payments", {
          body: { action: "public-payment-options", quote_id: quoteId },
        });
        if (cancelled || error || !data || data.error) return;
        setOptions(data as PublicPaymentOptions);
      } catch {
        // Offering the deposit alone is the safe fallback; nothing to surface.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [quoteId, enabled]);

  return options;
}

// Only a real partial deposit makes the choice meaningful: at 0% or 100% the
// two options would charge the same thing (or nothing).
export function hasDepositChoice(options: PublicPaymentOptions | null): options is PublicPaymentOptions {
  return !!options && options.deposit_amount > 0 && options.deposit_amount < options.total_amount;
}
